import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Copy,
  Database,
  Plus,
  RefreshCw,
  Trash2,
  Loader2,
} from "lucide-react";
import { useState, type MouseEvent, type KeyboardEvent } from "react";
import { api, ApiError } from "../api";
import type { SandboxInfo } from "../types";
import { StatusBadge } from "../components/StatusBadge";
import { EmptyState } from "../components/EmptyState";
import { Countdown } from "../components/Countdown";
import { relativeTime, useTick } from "../lib/time";

export function ListPage() {
  const queryClient = useQueryClient();
  const now = useTick(15_000); // drive relative time re-renders

  const {
    data: sandboxes,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: ["sandboxes"],
    queryFn: api.listSandboxes,
    refetchInterval: 5_000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteSandbox(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sandboxes"] });
    },
  });

  const list = sandboxes ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        onRefresh={() => refetch()}
        isFetching={isFetching}
        count={list.length}
      />

      {isLoading ? (
        <LoadingShell />
      ) : error ? (
        <ErrorShell error={error} onRetry={() => refetch()} />
      ) : list.length === 0 ? (
        <EmptyState />
      ) : (
        <SandboxTable
          sandboxes={list}
          onDelete={(id) => deleteMutation.mutate(id)}
          deletingId={deleteMutation.isPending ? deleteMutation.variables : null}
          now={now}
        />
      )}
    </div>
  );
}

function PageHeader({
  onRefresh,
  isFetching,
  count,
}: {
  onRefresh: () => void;
  isFetching: boolean;
  count: number;
}) {
  return (
    <div>
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="term-label mb-3">ps aux | grep simdb</div>
          <h1 className="font-serif text-5xl text-ink-100 leading-[0.95]">
            active <em className="text-phosphor-400">sandboxes</em>
          </h1>
          <p className="mt-3 text-sm text-ink-400">
            Ephemeral Postgres instances, seeded on demand.
            {count > 0 && (
              <>
                {" "}
                <span className="text-ink-200">{count}</span> currently tracked.
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            className="btn"
            disabled={isFetching}
            aria-label="Refresh"
          >
            <RefreshCw
              size={13}
              className={isFetching ? "animate-spin" : ""}
            />
            refresh
          </button>
          <Link to="/new" className="btn btn-primary">
            <Plus size={14} />
            new sandbox
          </Link>
        </div>
      </div>

      <AsciiRule />
    </div>
  );
}

function AsciiRule() {
  return (
    <div
      className="ascii-hr mt-6"
      aria-hidden="true"
      style={{ textOverflow: "clip" }}
    >
      {"+".padEnd(400, "-—")}
    </div>
  );
}

function LoadingShell() {
  return (
    <div className="border border-ink-800 bg-ink-900/60 p-12 text-center">
      <div className="inline-flex items-center gap-2 text-ink-400 text-sm">
        <Loader2 size={14} className="animate-spin text-phosphor-400" />
        <span className="caret">loading sandboxes</span>
      </div>
    </div>
  );
}

function ErrorShell({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  const msg =
    error instanceof ApiError
      ? `${error.status} — ${error.message}`
      : String(error);
  return (
    <div className="border border-danger-900 bg-danger-900/20 p-6 space-y-3">
      <div className="term-label !text-danger-400">stderr</div>
      <pre className="text-sm text-ink-100 whitespace-pre-wrap">{msg}</pre>
      <button type="button" onClick={onRetry} className="btn">
        retry
      </button>
    </div>
  );
}

function SandboxTable({
  sandboxes,
  onDelete,
  deletingId,
  now,
}: {
  sandboxes: SandboxInfo[];
  onDelete: (id: string) => void;
  deletingId: string | null | undefined;
  now: Date;
}) {
  const navigate = useNavigate();

  return (
    <div className="border border-ink-800 bg-ink-900">
      <div className="grid grid-cols-[minmax(120px,1.3fr)_90px_130px_minmax(220px,2.5fr)_110px_110px_120px] items-center px-5 py-3 border-b border-ink-800 bg-ink-850 text-[10px] uppercase tracking-widest text-ink-400">
        <div>id</div>
        <div>engine</div>
        <div>status</div>
        <div>prompt</div>
        <div>created</div>
        <div>ttl</div>
        <div className="text-right">actions</div>
      </div>

      {sandboxes.map((s, i) => (
        <SandboxRow
          key={s.id}
          sandbox={s}
          index={i}
          onDelete={() => onDelete(s.id)}
          onOpen={() => navigate(`/sandbox/${encodeURIComponent(s.id)}`)}
          deleting={deletingId === s.id}
          now={now}
        />
      ))}
    </div>
  );
}

function SandboxRow({
  sandbox,
  index,
  onDelete,
  onOpen,
  deleting,
  now,
}: {
  sandbox: SandboxInfo;
  index: number;
  onDelete: () => void;
  onOpen: () => void;
  deleting: boolean;
  now: Date;
}) {
  const [copied, setCopied] = useState(false);

  const copyId = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(sandbox.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };

  const onRowDelete = (e: MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Destroy sandbox ${sandbox.id.slice(0, 8)}? This cannot be undone.`)) {
      onDelete();
    }
  };

  const shortId = sandbox.id.slice(0, 10);
  const truncatedPrompt =
    sandbox.prompt.length > 80
      ? sandbox.prompt.slice(0, 80) + "…"
      : sandbox.prompt;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") onOpen();
      }}
      className="group grid grid-cols-[minmax(120px,1.3fr)_90px_130px_minmax(220px,2.5fr)_110px_110px_120px] items-center px-5 py-3.5 border-b border-ink-800 last:border-b-0 hover:bg-ink-850/70 transition-colors cursor-pointer animate-row-in"
      style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Database size={13} className="text-ink-500 shrink-0" />
        <code className="text-xs text-ink-100 truncate">{shortId}</code>
        <button
          type="button"
          onClick={copyId}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-ink-400 hover:text-phosphor-300"
          aria-label="Copy id"
        >
          {copied ? (
            <span className="text-[10px] text-signal-400">copied</span>
          ) : (
            <Copy size={11} />
          )}
        </button>
      </div>

      <div className="text-xs text-ink-300 lowercase">{sandbox.engine}</div>

      <div>
        <StatusBadge status={sandbox.status} />
      </div>

      <div
        className="text-xs text-ink-300 truncate pr-6"
        title={sandbox.prompt}
      >
        {truncatedPrompt}
      </div>

      <div
        className="text-xs text-ink-400"
        title={new Date(sandbox.created_at).toLocaleString()}
      >
        {relativeTime(sandbox.created_at, now)}
      </div>

      <div className="text-xs">
        <Countdown expiresAt={sandbox.expires_at} />
      </div>

      <div
        className="flex items-center justify-end gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onOpen}
          className="p-1.5 text-ink-400 hover:text-phosphor-300"
          aria-label="Open detail"
        >
          <ArrowUpRight size={14} />
        </button>
        <button
          type="button"
          onClick={onRowDelete}
          disabled={deleting}
          className="p-1.5 text-ink-400 hover:text-danger-400 disabled:opacity-40"
          aria-label="Delete sandbox"
        >
          {deleting ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Trash2 size={14} />
          )}
        </button>
      </div>
    </div>
  );
}
